<template>
    <div class="confirm-dialog modal fade" tabindex="-1" role="dialog" data-backdrop="static" aria-hidden="true">
        <div class="modal-dialog modal-xl">
            <div class="modal-content">
                <div class="modal-header bg-primary text-white">
                    <h5>{{ $t("dialogs.edit_help.title") }}</h5>
                    <button type="button" class="close text-white ml-0" data-dismiss="modal" aria-label="Close" @click="clear()">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="help-form col-9">
                        <div class="form row">
                            <div class="col-12">
                                <form @submit.prevent="submitForm()">
                                    <div class="form-group">
                                        <label for="role" class="col-form-label">{{ $t('labels.select_role') }}:</label>
                                        <select v-model="roleId" id="role" class="custom-select">
                                            <option value="" hidden>{{ $t('placeholders.role') }}</option>
                                            <option v-for="role in roles" :key="role.id" :value="role.id">{{ $t("roles." + role.key) }}</option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label for="name_of_page" class="col-form-label">{{ $t('labels.page') }}:</label>
                                        <select v-model="nameOfPage" id="name_of_page" class="custom-select">
                                            <option value="" hidden>{{ $t('placeholders.name_of_page') }}</option>
                                            <option v-for="page in pages" :key="page.name" :value="page.name">
                                                {{ $t("pages." + page.name) }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label for="language" class="col-form-label">
                                            {{ $t('labels.language')}}:
                                        </label>
                                        <select v-model="language" id="language" class="custom-select">
                                            <option value="" hidden>{{ $t('placeholders.language') }}</option>
                                            <option v-for="language in languages" :key="language.code" :value="language.code">
                                                {{ $t("languages." + language.code) }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label for="content" class="col-form-label">{{ $t('labels.text') }}:</label>
                                        <VueEditor v-model="content" id="content" :editorToolbar="editorToolbar"/>
                                        <div v-if="contentError" class="invalid-feedback d-block">
                                            {{ $t('general.error') }}: {{ contentError }}
                                        </div>
                                    </div>

                                    <div class="form-group">
                                        <label class="col-form-label">{{ $t('labels.video') }}:</label>
                                        <form id="videoUpload" v-if="!video">
                                            <input type="file" id="fileUpload" class="form-group" accept="video/mp4,video/avi,video/mpeg,video/mpeg4" @input="saveVideo()">
                                        </form>
                                        <div class="text-center mb-5" id="videoDiv" v-else>
                                            <button type="button" class="close text-danger btn-lg" @click="deleteVideo()">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <video controls id="videoTag" v-html="video"></video>
                                        </div>
                                    </div>

                                    <div class="messages">
                                        <AlertMessage
                                            v-for="(message, index) in messages"
                                            :key="index"
                                            :type="message.type"
                                            :text="message.text"
                                            :closable="false"/>
                                    </div>

                                    <div class="button-row row">
                                        <LoadingButton
                                            class="mr-2"
                                            type="submit"
                                            color="success" 
                                            :label="$t('buttons.save')" 
                                            :loadingLabel="$t('buttons.saving')" 
                                            :loading="saving"
                                        >
                                            <template slot="icon-before">
                                                <i class="fas fa-check mr-1"></i>
                                            </template>
                                            </LoadingButton>
                                        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="clear()">
                                            {{ $t("buttons.close") }}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import {VueEditor} from "vue2-editor";
import AlertMessage from "../AlertMessage";
import { useRootStore } from '../../stores/root';
import utils from '../../utils/utils';
import Http from '../../utils/http';

export default {
    name: "EditHelpDialog",

    components: {
        VueEditor,
        AlertMessage
    },

    data() {
        return {
            roleId: '',
            pages: [],
            nameOfPage: '',
            content: '',
            language: '',
            video: '',
            setByHelpDialog: false,
            saving: false,

            languages: utils.LANGUAGES,
            
            editorToolbar: [
                [{size: ['small', 'normal', 'large']}],
                [{header: 1}, {header: 2}],
                ['bold', 'italic', 'underline'],
                [{list: 'ordered'}, {list: 'bullet'}],
                [{align: ''}, {align: 'center'}, {align: 'right'}, {align: 'justify'}],
                ['link', 'image'],
            ],
            roles: [],
            messages: []
        }
    },
    
    created() {
        Http.get('/api/roles').then(({ data }) => {
            this.roles = data;  
        })
    },

    computed: {
        ...mapStores(useRootStore),
        
        contentError() {
            return this.rootStore.errors['content'];
        }
    },

    watch: {
        roleId() {
            if (this.roleId && !this.setByHelpDialog) {
                this.messages = [];
                this.nameOfPage = '';
                this.language = '';
                this.content = '';
                this.video = '';
                this.getPages();
                $("#videoUpload").trigger("reset");
            }
        },

        nameOfPage() {
            if (this.nameOfPage && !this.setByHelpDialog) {
                this.messages = [];
                this.language = '';
                this.content = '';
                this.video = '';
                $("#videoUpload").trigger("reset");
            }
        },

        language() {
            if (this.language && !this.setByHelpDialog) {
                this.messages = [];
                this.getText();
            } else if (this.setByHelpDialog) {
                this.setByHelpDialog = false;
            }
        },
    },

    methods: {
        getText() {
            this.rootStore.getHelpText(this.roleId, this.nameOfPage, this.language)
                .then(response => {
                    this.content = response["text"];
                    this.video = response["video"];
                })
                .catch(() => {
                    this.content = '';
                    this.video = '';
                });
        },

        async getPages() {
            try {
                this.pages = await this.rootStore.getPages(this.roleId)
            } catch (error) {
                this.pages = [];
            }
        },

        setParameters(roleId, pages, nameOfPage, language) {
            this.setByHelpDialog = true;
            this.roleId = roleId;
            this.pages = pages;
            this.nameOfPage = nameOfPage;
            this.language = language;
            this.getText();
        },

        saveVideo() {
            var fileInput = document.getElementById('fileUpload');

            if (fileInput.files.length > 0) {
                var reader = new FileReader();
                reader.readAsDataURL(fileInput.files[0]);
                reader.onload = () => {
                    this.video = '<source src="' + reader.result + '">';
                };
            }
        },

        submitForm() {
            this.messages = [];

            if(this.content == null || this.content == "") {
                this.content = this.language == "SK" 
                    ? "<strong>Pomocník pre túto stránku neexistuje.</strong>" 
                    : "<strong>Help for this page doesn't exist.</strong>";
            }
            
            const data = {
                role_id: this.roleId,
                content: this.content,
                name_of_page: this.nameOfPage,
                video: this.video,
                language: this.language
            };
            
            this.saving = true;

            this.rootStore.editHelp(data)
                .then(() => {
                    this.messages.push({
                        type: 'success',
                        text: this.$t('notifications.help_successfully_edited')
                    });
                }).catch(() => {
                    this.messages.push({
                        type: 'warning',
                        text: this.$t('notifications.help_not_edited')
                    });
                }).finally(() => {
                    this.saving = false;
                });
        },

        clear() {
            this.messages = [];
            this.roleId = '';
            this.nameOfPage = '';
            this.language = '';
            this.content = '';
            this.video = '';
            $("#videoUpload").trigger("reset");
            $(".help-modal").css("display", "block");
        },

        deleteVideo() {
            this.video = "";
        }
    }
}
</script>

<style scoped>
.confirm-dialog .modal-content {
    border: 0;
}

.confirm-dialog .modal-header h5 {
    margin: 0;
}

.confirm-dialog .modal-body {
    position: relative;
    min-height: 130px;
    height: auto;
    background-color: whitesmoke;
}

.modal-body .row-button {
    position: absolute;
    bottom: 1em;
    right: 1em;
    margin: 0;
    padding: 0;
}

.modal-body .row-button button {
    margin-left: 0.5em;
}

.help-form {
    padding: 1.5em 0;
}

.help-form .form {
    justify-content: center;
}

.help-form .form .form-group label {
    font-weight: bold;
}

.help-form .form .button-row {
    margin: 1em 0 0;
    justify-content: flex-end;
}

.modal-body {
    justify-content: center;
    display: flex;
}

.modal-dialog {
    max-width: 1170px !important;
}

#videoTag {
    max-width: 100%;
}
</style>
